import { ElementApi, NodeApi, TextApi } from 'platejs'
import type {
  EditorNodesOptions,
  NodeEntry,
  TElement,
  TNode,
  TText,
} from 'platejs'

import type { SerializedNode } from './types'
import type { BufferEditor, BufferValue } from './types.plate'
import type { SlateEntityData } from '../helpers/EditorEntityData'
import { FacebookMentionInputElement } from '../plugins/facebook-mention/nodes'
import type { FacebookMentionEntityDetails } from '../plugins/facebook-mention/nodes'
import { LinkElement } from '../plugins/link/nodes/LinkElement'
import type { LinkEntity } from '../plugins/link/nodes/LinkElement'
import { LinkedInAnnotationInputElement } from '../plugins/linkedin-annotations/nodes'
import type { LinkedInAnnotationEntityDetails } from '../plugins/linkedin-annotations/nodes'

/**
 * Paragraphs should add a line break at the end of the serlized text when
 *  - when the node being serialized is the last one in the paragraph
 *  - AND it's not the last paragraph in an editor
 */
const shouldAddLineBreak = (
  editor: BufferEditor,
  [, path]: NodeEntry<TText>,
): boolean => {
  const [firstLevel] = path

  if (firstLevel === undefined) {
    // this is just the editor node
    return false
  }
  const firstLevelNode = NodeApi.get<TElement>(editor, [firstLevel])
  if (firstLevelNode && firstLevelNode?.type === 'p') {
    // `isLastChild`'s `root` is the tree to resolve `path` against — both
    // checks must root from the editor since `path` is already absolute.
    // A text node nested inside an inline element (link, mention) is only
    // the paragraph's last text if every ancestor level between it and the
    // paragraph is itself the last child of its own parent.
    let isLastNodeInPararaph = true
    for (let depth = path.length; depth > 1; depth--) {
      if (!NodeApi.isLastChild(editor, path.slice(0, depth))) {
        isLastNodeInPararaph = false
        break
      }
    }
    const isLastParagraph = NodeApi.isLastChild(editor, [firstLevel])

    return isLastNodeInPararaph && !isLastParagraph
  }

  return false
}

export const serializeNode = (
  editor: BufferEditor,
  [node, path]: NodeEntry<TNode>,
): SerializedNode<
  | LinkEntity
  | LinkedInAnnotationEntityDetails
  | FacebookMentionEntityDetails
  | undefined
> => {
  if (TextApi.isText(node)) {
    const text = shouldAddLineBreak(editor, [node, path])
      ? `${node.text}\n`
      : node.text

    return { text }
  }

  if (NodeApi.isEditor(node)) return { text: '' }

  if (LinkElement.is(node)) return LinkElement.serialize(node)
  if (LinkedInAnnotationInputElement.is(node))
    return LinkedInAnnotationInputElement.serialize(node)
  if (FacebookMentionInputElement.is(node))
    return FacebookMentionInputElement.serialize(node)

  return { text: '' }
}

export const serialize = (
  editor: BufferEditor,
  options: EditorNodesOptions<BufferValue> = {},
): { text: string; data: SlateEntityData } => {
  const nodeEntries = editor.api.nodes({
    at: [],
    ...options,
    // we have to go forward to avoid having to recalculate textBefore
    reverse: false,
  })

  const serializedValue = Array.from(nodeEntries).reduce(
    (result: { text: string; data: SlateEntityData }, [node, path]) => {
      const textBefore = result.text
      const { text, data } = serializeNode(editor, [node, path])

      result.text += text

      if (ElementApi.isElement(node) && data) {
        const length = text.length || editor.api.string(path).length
        const position = { start: textBefore.length, length }

        if (LinkElement.is(node)) {
          const entities = result.data[LinkElement.type] ?? []
          result.data[LinkElement.type] = [
            ...entities,
            { ...LinkElement.serialize(node).data, ...position },
          ]
        } else if (LinkedInAnnotationInputElement.is(node)) {
          const entity = LinkedInAnnotationInputElement.serialize(node).data
          if (entity) {
            const entities =
              result.data[LinkedInAnnotationInputElement.type] ?? []
            result.data[LinkedInAnnotationInputElement.type] = [
              ...entities,
              { ...entity, ...position },
            ]
          }
        } else if (FacebookMentionInputElement.is(node)) {
          const entity = FacebookMentionInputElement.serialize(node).data
          if (entity) {
            const entities = result.data[FacebookMentionInputElement.type] ?? []
            result.data[FacebookMentionInputElement.type] = [
              ...entities,
              { ...entity, ...position },
            ]
          }
        }
      }

      return result
    },
    { text: '', data: {} },
  )

  return serializedValue
}
